import React, { useState } from "react";
import { Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";

import * as SubframeCore from "@subframe/core";
import { Button } from "subframe/components/Button";
import { TextArea, IconButton, ToggleGroup } from "subframe";

import { isEmpty as isStringEmpty } from "utils/string";

import { EVENT_TYPES } from "constants/mixpanel";
import { mixpanelTrackEvent } from "services/mixpanel";

import { followUpEscalation } from "modules/Retention/services/retention";
import { ESCALATION_FOLLOW_UP_OUTCOME } from "modules/Retention/constants/constants";

function EscalationFollowUp({
  flat = false,
  focused = false,
  escalationId,
  employeeId,
  canMinimize = false,
  isMinimized = false,
  onSubmit = () => {},
  onMinimize = () => {},
}) {
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState(
    ESCALATION_FOLLOW_UP_OUTCOME.SUCCESSFUL.value
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [isSuccessMessageShown, setIsSuccessMessageShown] = useState(false);
  const [hasTriedFormSubmission, setHasTriedFormSubmission] = useState(false);

  const isFormValid = !isStringEmpty(outcome) && !isStringEmpty(notes);

  const resetForm = () => {
    setOutcome(ESCALATION_FOLLOW_UP_OUTCOME.SUCCESSFUL.value);
    setNotes("");
    setHasTriedFormSubmission(false);
  };

  /**
   * Submits the escalation follow-up form.
   *
   * @param {Object} payload
   * @returns {Promise}
   */
  const submitEscalationFollowUpAsync = async (payload) => {
    try {
      return await followUpEscalation(payload);
    } catch (error) {
      return { hasError: true, error };
    }
  };

  /**
   * Handles the submission of the escalation follow-up.
   */
  const handleFormSubmit = async () => {
    setHasTriedFormSubmission(true);

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);
    const response = await submitEscalationFollowUpAsync({
      escalation_id: +escalationId,
      employee_id: employeeId,
      outcome,
      notes,
    });

    setIsSubmitting(false);

    mixpanelTrackEvent(EVENT_TYPES.LOG_FOLLOW_UP_SEND);

    if (response?.hasError) {
      setErrorMessage(
        `Error while submitting: ${response?.error?.response?.data?.message}`
      );
      setIsErrorMessageShown(true);

      return;
    }

    resetForm();
    setSuccessMessage("Successfully logged follow-up.");
    setIsSuccessMessageShown(true);
    onSubmit();
    onMinimize();
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  /**
   * Renders the success snackbar.
   *
   * @returns {Component}
   */
  const renderSuccessSnackbar = () => (
    <Snackbar
      open={isSuccessMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsSuccessMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsSuccessMessageShown(false)}
        severity="success"
        sx={{ width: "100%" }}
      >
        {successMessage}
      </MuiAlert>
    </Snackbar>
  );

  return (
    <>
      {!isMinimized && (
        <div
          className={SubframeCore.twClassNames(
            "w-full border-2 border-solid border-neutral-border rounded flex-col shadow items-start flex bg-default-background group/a7126f05 sticky bottom-0",
            {
              "border-2 border-solid border-default-background shadow-none":
                flat,
              "border-2 border-solid border-brand-primary": focused,
            }
          )}
        >
          <div className="w-full gap-2 pt-3 pr-3 pl-5 items-center flex">
            <div className="grow shrink-0 basis-0 w-full gap-2 items-center flex">
              <SubframeCore.Icon
                className="text-brand-600 text-body font-body"
                name="FeatherMessageSquarePlus"
              />
              <span className="text-default-font text-body-bold font-body-bold">
                Log Follow-up
              </span>
            </div>
            {canMinimize && (
              <IconButton
                icon="FeatherMinimize2"
                size="small"
                title="Minimize"
                onClick={onMinimize}
              />
            )}
          </div>
          <div className="w-full flex-col gap-2 pt-4 pr-4 pb-4 pl-4 items-start flex">
            <ToggleGroup
              className="flex-none h-auto w-full"
              value={outcome}
              onValueChange={setOutcome}
            >
              <ToggleGroup.Item
                className="grow shrink-0 basis-0 w-full h-10 justify-center"
                icon="FeatherCheck"
                value={ESCALATION_FOLLOW_UP_OUTCOME.SUCCESSFUL.value}
              >
                {ESCALATION_FOLLOW_UP_OUTCOME.SUCCESSFUL.label}
              </ToggleGroup.Item>
              <ToggleGroup.Item
                className="grow shrink-0 basis-0 w-full h-10 justify-center"
                icon="FeatherX"
                value={ESCALATION_FOLLOW_UP_OUTCOME.DISMISSED.value}
              >
                {ESCALATION_FOLLOW_UP_OUTCOME.DISMISSED.label}
              </ToggleGroup.Item>
            </ToggleGroup>
            <TextArea
              onChange={(event) => {
                setNotes(event?.target?.value);
              }}
              className="flex-none h-auto w-full"
              label=""
              helpText={
                hasTriedFormSubmission && isStringEmpty(notes)
                  ? "Notes is required"
                  : ""
              }
              error={hasTriedFormSubmission && isStringEmpty(notes)}
            >
              <TextArea.Input
                value={notes}
                placeholder="Add follow-up notes..."
              />
            </TextArea>
            <div className="w-full gap-2 justify-end flex">
              <Button
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={handleFormSubmit}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
      {renderErrorSnackbar()}
      {renderSuccessSnackbar()}
    </>
  );
}

export default EscalationFollowUp;
